import { safeApiRequest, apiRequest } from './apiClient';
import { sanitizeSearchInput } from '../utils/security';

const TECH_SERVICES_ENDPOINT = '/api/tech-services';

/**
 * Teknik servis payload'ını oluşturur
 */
const buildTechServicePayload = (payload = {}) => {
  const data = {};

  if (payload.ad !== undefined && payload.ad !== '') {
    data.ad = String(payload.ad).trim();
  }

  if (payload.adres !== undefined && payload.adres !== '') {
    data.adres = String(payload.adres).trim();
  }

  if (payload.telefon !== undefined && payload.telefon !== '') {
    data.telefon = String(payload.telefon).trim();
  }

  if (payload.mail !== undefined && payload.mail !== '') {
    data.mail = String(payload.mail).trim().toLowerCase();
  }

  if (payload.cityId !== undefined) {
    data.cityId = parseInt(payload.cityId, 10);
  }

  if (payload.districtId !== undefined) {
    data.districtId = parseInt(payload.districtId, 10);
  }

  // Konum bilgisi: yeni format (konum objesi) veya eski format (enlem/boylam direkt)
  if (payload.konum !== undefined && payload.konum !== null) {
    // Yeni format: konum objesi direkt kullan
    data.konum = payload.konum;
  } else if (payload.enlem !== undefined && payload.boylam !== undefined) {
    // Eski format: enlem ve boylam'dan konum objesi oluştur
    const enlem = parseFloat(payload.enlem);
    const boylam = parseFloat(payload.boylam);
    const zoom = payload.zoom !== undefined ? Math.max(0, Math.min(20, parseInt(payload.zoom, 10))) : 15;
    
    if (!isNaN(enlem) && !isNaN(boylam)) {
      data.konum = {
        koordinat: {
          enlem: enlem,
          boylam: boylam,
        },
        zoom: zoom,
      };
    }
  }

  if (payload.yetkiliKisi !== undefined && payload.yetkiliKisi !== '') {
    data.yetkiliKisi = String(payload.yetkiliKisi).trim();
  }

  if (payload.yetkiliTelefon !== undefined && payload.yetkiliTelefon !== '') {
    data.yetkiliTelefon = String(payload.yetkiliTelefon).trim();
  }

  if (payload.faks !== undefined && payload.faks !== null && String(payload.faks).trim() !== '') {
    data.faks = String(payload.faks).trim();
  }

  if (payload.katilmaTarihi !== undefined && payload.katilmaTarihi !== null && String(payload.katilmaTarihi).trim() !== '') {
    const d = String(payload.katilmaTarihi).trim();
    data.katilmaTarihi = d.length === 10 ? `${d}T12:00:00.000Z` : d;
  }

  if (payload.aciklama !== undefined && payload.aciklama !== '') {
    data.aciklama = String(payload.aciklama).trim();
  }

  // Şirket bilgileri - yeni format (array)
  if (payload.sirketBilgileri !== undefined && Array.isArray(payload.sirketBilgileri)) {
    // Array formatını direkt kullan (boş array bile olsa)
    data.sirketBilgileri = payload.sirketBilgileri;
  }
  
  // Şirket bilgileri - eski format (tek tek alanlar) - geriye dönük uyumluluk için
  if (payload.sirketTipi !== undefined && payload.sirketTipi !== '') {
    data.sirketTipi = String(payload.sirketTipi).trim();
  }

  if (payload.sirketAdi !== undefined && payload.sirketAdi !== '') {
    data.sirketAdi = String(payload.sirketAdi).trim();
  }

  if (payload.vergiDairesi !== undefined && payload.vergiDairesi !== '') {
    data.vergiDairesi = String(payload.vergiDairesi).trim();
  }

  if (payload.vergiNumarasi !== undefined && payload.vergiNumarasi !== '') {
    data.vergiNumarasi = String(payload.vergiNumarasi).trim();
  }

  if (payload.sirketAdresi !== undefined && payload.sirketAdresi !== '') {
    data.sirketAdresi = String(payload.sirketAdresi).trim();
  }

  // Banka bilgileri
  if (payload.bankaBilgileri !== undefined && Array.isArray(payload.bankaBilgileri) && payload.bankaBilgileri.length > 0) {
    data.bankaBilgileri = payload.bankaBilgileri.map(banka => ({
      bankaId: banka.bankaId || undefined,
      hesapAdi: banka.hesapAdi ? String(banka.hesapAdi).trim() : undefined,
      iban: banka.iban ? String(banka.iban).trim().replace(/\s/g, '').toUpperCase() : undefined,
    })).filter(banka => banka.bankaId && banka.hesapAdi && banka.iban);
  }

  if (payload.aktif !== undefined) {
    data.aktif = Boolean(payload.aktif);
  }

  if (payload.isTest !== undefined) {
    data.isTest = payload.isTest === true;
  }

  return data;
};

/**
 * Tüm teknik servisleri getirir (sayfalama ile)
 */
export const fetchTechServices = async ({
  page = 1,
  limit = 50,
  cityId,
  districtId,
  aktif = true,
  search,
  sortBy,
  sortOrder,
} = {}) => {
  const queryParams = {};
  
  if (page) {
    queryParams.sayfa = page;
  }

  queryParams.limit = Math.min(Math.max(parseInt(limit, 10) || 50, 1), 100);

  if (sortBy === 'ad' || sortBy === 'il') {
    queryParams.sortBy = sortBy;
  }
  if (sortOrder === 'asc' || sortOrder === 'desc') {
    queryParams.sortOrder = sortOrder;
  }
  
  if (cityId) {
    queryParams.cityId = parseInt(cityId, 10);
  }
  
  if (districtId) {
    queryParams.districtId = parseInt(districtId, 10);
  }
  
  // aktif parametresini sadece false ise gönder, varsayılan true olduğu için
  if (aktif === false) {
    queryParams.aktif = 'false';
  }
  
  // Arama parametresini gönder (boş string değilse)
  if (search && search.trim().length > 0) {
    queryParams.arama = sanitizeSearchInput(search);
  }
  
  console.log('[fetchTechServices] Query params:', queryParams);
  
  return safeApiRequest(TECH_SERVICES_ENDPOINT, {
    method: 'GET',
    query: queryParams,
    skipCsrf: true,
  });
};

/**
 * Teknik servis listesi (select için)
 */
let cachedTechServicesSelect = null;
let techServicesSelectCacheExpiry = 0;

export const fetchTechServicesSelect = async ({ cityId, districtId, aktif = true } = {}) => {
  const now = Date.now();
  if (cachedTechServicesSelect && techServicesSelectCacheExpiry > now) {
    return cachedTechServicesSelect;
  }

  const queryParams = {};
  if (cityId) {
    queryParams.cityId = parseInt(cityId, 10);
  }
  if (districtId) {
    queryParams.districtId = parseInt(districtId, 10);
  }
  if (aktif === false) {
    queryParams.aktif = 'false';
  }

  const response = await apiRequest('/api/tech-services/select', {
    method: 'GET',
    query: queryParams,
    skipCsrf: true,
  });

  cachedTechServicesSelect = response;
  techServicesSelectCacheExpiry = now + 1000 * 60; // 1 dakika cache
  return response;
};

export const invalidateTechServicesSelectCache = () => {
  cachedTechServicesSelect = null;
  techServicesSelectCacheExpiry = 0;
};

/**
 * Teknik servis detayı
 */
export const fetchTechServiceDetail = async (id) => {
  if (!id) {
    throw new Error('Teknik servis ID gereklidir');
  }
  return safeApiRequest(`${TECH_SERVICES_ENDPOINT}/${id}`, {
    method: 'GET',
    skipCsrf: true,
  });
};

/**
 * Teknik servis arama
 * Endpoint: GET /api/tech-services/search
 * Dokümantasyon: docs/TECH_SEARCH.md
 */
export const searchTechServices = async (
  arama,
  { cityId, districtId, aktif, sayfa, limit, sortBy, sortOrder } = {}
) => {
  if (!arama || arama.trim().length < 2) {
    throw new Error('Arama terimi en az 2 karakter olmalıdır');
  }
  
  const queryParams = {
    arama: sanitizeSearchInput(arama),
  };
  
  if (cityId) {
    queryParams.cityId = parseInt(cityId, 10);
  }
  
  if (districtId) {
    queryParams.districtId = parseInt(districtId, 10);
  }
  
  // Aktif durumu parametresi (true/false veya undefined)
  if (aktif !== undefined) {
    queryParams.aktif = aktif === true ? 'true' : 'false';
  }
  
  // Pagination parametreleri
  if (sayfa !== undefined && sayfa !== null) {
    queryParams.sayfa = sayfa;
  }
  
  if (limit !== undefined && limit !== null) {
    queryParams.limit = Math.min(limit, 100); // Max 100
  }

  if (sortBy === 'ad' || sortBy === 'il') {
    queryParams.sortBy = sortBy;
  }
  if (sortOrder === 'asc' || sortOrder === 'desc') {
    queryParams.sortOrder = sortOrder;
  }
  
  console.log('[searchTechServices] Searching with params:', queryParams);
  
  return safeApiRequest('/api/tech-services/search', {
    method: 'GET',
    query: queryParams,
    skipCsrf: true,
  });
};

/**
 * AP — Excel dışa aktarım: filtrelenmiş tüm teknik servis kayıtları (yalnız admin).
 * GET /api/tech-services/export
 */
export const fetchTechServicesExport = async ({
  cityId,
  districtId,
  aktif = true,
  arama,
  sortBy,
  sortOrder,
} = {}) => {
  const queryParams = {};

  if (sortBy === 'ad' || sortBy === 'il') {
    queryParams.sortBy = sortBy;
  }
  if (sortOrder === 'asc' || sortOrder === 'desc') {
    queryParams.sortOrder = sortOrder;
  }

  if (cityId !== undefined && cityId !== '' && cityId !== 'all') {
    queryParams.cityId = parseInt(cityId, 10);
  }

  if (districtId !== undefined && districtId !== '') {
    queryParams.districtId = parseInt(districtId, 10);
  }

  if (aktif === false) {
    queryParams.aktif = 'false';
  } else {
    queryParams.aktif = 'true';
  }

  if (arama && String(arama).trim().length >= 3) {
    queryParams.arama = sanitizeSearchInput(arama);
  }

  return safeApiRequest(`${TECH_SERVICES_ENDPOINT}/export`, {
    method: 'GET',
    query: queryParams,
    skipCsrf: true,
  });
};

/**
 * Teknik servis oluşturur
 */
export const createTechService = async (payload) => {
  const sanitizedPayload = buildTechServicePayload(payload);
  console.log('[TechServicesService] Creating tech service:', {
    endpoint: TECH_SERVICES_ENDPOINT,
    method: 'POST',
    payload: sanitizedPayload
  });
  
  return safeApiRequest(TECH_SERVICES_ENDPOINT, {
    method: 'POST',
    body: sanitizedPayload,
    skipCsrf: false, // POST için CSRF token gerekli
  });
};

/**
 * Teknik servis günceller
 */
export const updateTechService = async (id, payload) => {
  if (!id) {
    throw new Error('Teknik servis ID gereklidir');
  }
  const sanitizedPayload = buildTechServicePayload(payload);
  console.log('[TechServicesService] Updating tech service:', {
    endpoint: `${TECH_SERVICES_ENDPOINT}/${id}`,
    method: 'PUT',
    payload: sanitizedPayload
  });
  
  return safeApiRequest(`${TECH_SERVICES_ENDPOINT}/${id}`, {
    method: 'PUT',
    body: sanitizedPayload,
    skipCsrf: false, // PUT için CSRF token gerekli
  });
};

/**
 * Teknik servis siler (soft delete)
 */
export const deleteTechService = async (id) => {
  if (!id) {
    throw new Error('Teknik servis ID gereklidir');
  }
  return safeApiRequest(`${TECH_SERVICES_ENDPOINT}/${id}`, {
    method: 'DELETE',
    skipCsrf: false, // DELETE için CSRF token gerekli
  });
};

export const fetchKonsinyeCariOzet = async (id) => {
  if (!id) throw new Error('Teknik servis ID gereklidir');
  return safeApiRequest(`${TECH_SERVICES_ENDPOINT}/${id}/konsinye-cari-ozet`, {
    method: 'GET',
  });
};

export const fetchKonsinyeCariBulk = async (ids) => {
  const list = Array.isArray(ids) ? ids.filter(Boolean) : [];
  if (list.length === 0) return { basarili: true, veri: {} };
  return safeApiRequest(`${TECH_SERVICES_ENDPOINT}/konsinye-cari-bulk`, {
    method: 'GET',
    query: { ids: list.join(',') },
  });
};

export const updateCariLimit = async (id, cariLimit) => {
  if (!id) throw new Error('Teknik servis ID gereklidir');
  return safeApiRequest(`${TECH_SERVICES_ENDPOINT}/${id}/cari-limit`, {
    method: 'PATCH',
    body: { cariLimit: cariLimit ?? null },
    skipCsrf: false,
  });
};

